/**
 * The product page: gallery, options, quantity.
 *
 * The gallery is one track of photographs, scrolled by a finger, by the
 * arrows under it, by a thumbnail or by picking a colour. It used to be a
 * main image that the script swapped plus a grid of thumbnails, which meant
 * the photographs existed twice and, on a phone, sixteen dead squares sat
 * between the shirt and the button.
 *
 * The track shows one colour at a time. Shopify attaches the front shot to
 * the variant and leaves the back and the side views attached to nothing,
 * so Liquid stamps each photograph with the colour whose run it falls in
 * and this only has to filter on it.
 *
 * Nothing starts chosen. Add to Cart is disabled until the selection names
 * exactly one variant, so the page never adds a size the visitor did not
 * pick. The radios carry their own checked state in CSS.
 */
(() => {
  const root = document.querySelector('[data-pdp]');
  if (!root) return;

  const data = document.querySelector('[data-pdp-variants]');
  if (!data) return;

  let variants;
  try {
    variants = JSON.parse(data.textContent);
  } catch (e) {
    return;
  }

  const colourPos = Number(root.dataset.colourPos);
  const sizePos = Number(root.dataset.sizePos);

  const track = document.querySelector('[data-pdp-track]');
  const slides = track ? [...track.children] : [];
  const thumbs = [...document.querySelectorAll('[data-pdp-thumbs] li')];
  const count = document.querySelector('[data-pdp-count]');
  const prev = document.querySelector('[data-pdp-prev]');
  const next = document.querySelector('[data-pdp-next]');
  const colours = [...document.querySelectorAll('[data-pdp-color]')];
  const sizes = [...document.querySelectorAll('[data-pdp-size]')];
  const price = document.querySelector('[data-pdp-price]');
  const idField = document.querySelector('[data-pdp-id]');
  const submit = document.querySelector('[data-pdp-submit]');
  const submitLabel = document.querySelector('[data-pdp-submit-label]');
  const basePrice = price ? price.textContent.trim() : '';
  const addLabel = (submit && submit.dataset.addLabel) || 'Add to Cart';
  const soldLabel = (submit && submit.dataset.soldLabel) || 'Sold Out';
  const chooseLabel = (submit && submit.dataset.chooseLabel) || 'Choose Options';

  // The field only submits once this file is running. Without it the
  // noscript select is what names the variant.
  if (idField) idField.name = 'id';

  const chosen = (list) => {
    const hit = list.find((input) => input.checked);
    return hit ? hit.value : null;
  };

  const optionAt = (variant, pos) => (pos >= 0 ? variant.options[pos] : null);

  const matches = (variant, colour, size) =>
    (colourPos < 0 || optionAt(variant, colourPos) === colour) &&
    (sizePos < 0 || optionAt(variant, sizePos) === size);

  /* ---- the gallery ---------------------------------------------- */

  const shown = () => slides.filter((s) => !s.hidden);

  // Held rather than read back from scrollLeft on every press: a smooth
  // scroll is still travelling when the next press arrives, so two taps on
  // the arrow moved one photograph.
  let at = 0;

  const paint = () => {
    const visible = shown();
    if (!visible.length) return;
    at = Math.min(visible.length - 1, Math.max(0, at));

    // Nothing to page through, nothing to page with. A colour can have one
    // photograph while the product has ten, so this cannot be decided in
    // Liquid from the product's own count.
    const many = visible.length > 1;
    if (prev) prev.hidden = !many;
    if (next) next.hidden = !many;
    if (count) count.hidden = !many;

    if (count) count.textContent = `${at + 1} / ${visible.length}`;

    const id = visible[at] && visible[at].dataset.slide;
    thumbs.forEach((li) => {
      const button = li.querySelector('[data-thumb-id]');
      if (button) button.classList.toggle('is-chosen', button.dataset.thumbId === id);
    });
  };

  // Round rather than stopping at the ends: past the last photograph is
  // the first one again. Stopping there means walking the whole reel back
  // to reach the other arrow.
  // Instant, never smooth. This track snaps mandatorily, and a mandatory
  // snap cancels a smooth programmatic scroll on the way: the arrows, the
  // thumbnails and picking a colour all left it exactly where it was. The
  // rows elsewhere snap by proximity and animate fine. A finger scrolls
  // this one natively either way.
  const goTo = (index) => {
    const visible = shown();
    if (!track || !visible.length) return;
    at = ((index % visible.length) + visible.length) % visible.length;
    track.scrollTo({ left: visible[at].offsetLeft, behavior: 'auto' });
    paint();
  };

  const step = (by) => goTo(at + by);

  // A finger moved it, so the counter follows the scroll rather than the
  // other way round.
  const readScroll = () => {
    const visible = shown();
    if (!visible.length || !track) return;
    let nearest = 0;
    let best = Infinity;
    visible.forEach((s, i) => {
      const gap = Math.abs(s.offsetLeft - track.scrollLeft);
      if (gap < best) {
        best = gap;
        nearest = i;
      }
    });
    at = nearest;
    paint();
  };

  // Only one colour's photographs are in the track at a time: the visitor
  // asked to see a white shirt, not every shirt.
  const showGroup = (colour) => {
    if (!track || colourPos < 0) return;
    const wanted = slides.filter((s) => s.dataset.colour === colour);
    const group = wanted.length ? colour : null;

    slides.forEach((s) => {
      s.hidden = group !== null && s.dataset.colour !== group;
    });
    thumbs.forEach((li) => {
      li.hidden = group !== null && li.dataset.colour !== group;
    });

    goTo(0);
  };

  if (track) {
    let ticking;
    track.addEventListener('scroll', () => {
      clearTimeout(ticking);
      ticking = setTimeout(readScroll, 120);
    });
  }

  if (prev) prev.addEventListener('click', () => step(-1));
  if (next) next.addEventListener('click', () => step(1));

  thumbs.forEach((li) => {
    const button = li.querySelector('[data-thumb-id]');
    if (!button) return;
    button.addEventListener('click', () => {
      goTo(shown().findIndex((s) => s.dataset.slide === button.dataset.thumbId));
    });
  });

  /* ---- the choices ---------------------------------------------- */

  // Which sizes exist in the colour on screen. Asked of the variants rather
  // than of the option's values, because the answer differs by colour.
  const gradeSizes = (colour) => {
    if (sizePos < 0) return;
    sizes.forEach((input) => {
      const wrap = input.closest('[data-pdp-size-wrap]');
      const made = variants.some(
        (v) =>
          optionAt(v, sizePos) === input.value &&
          (colourPos < 0 || colour === null || optionAt(v, colourPos) === colour) &&
          v.available
      );
      input.disabled = !made;
      if (wrap) wrap.classList.toggle('is-unavailable', !made);
      if (!made && input.checked) input.checked = false;
    });
  };

  // The names of what is chosen, written beside the labels. Empty until
  // something is picked: the ": " belongs to the value, not the label.
  const colourName = document.querySelector('[data-pdp-colour-name]');
  const sizeName = document.querySelector('[data-pdp-size-name]');

  // The line that says what is still missing, and the words for the two
  // choices, taken from the labels on screen rather than written here.
  const missing = document.querySelector('[data-pdp-missing]');
  const colourMessage = (missing && missing.dataset.colourMessage) || 'Pick a colour to carry on';
  const sizeMessage = (missing && missing.dataset.sizeMessage) || 'Pick a size to carry on';

  const refresh = () => {
    const colour = chosen(colours);
    gradeSizes(colour);

    if (missing && colour && chosen(sizes)) missing.classList.add('hidden');

    if (colourName) colourName.textContent = colour || '';
    if (sizeName) sizeName.textContent = chosen(sizes) || '';

    const needsColour = colourPos >= 0 && !colour;
    const needsSize = sizePos >= 0 && !chosen(sizes);
    const variant =
      needsColour || needsSize ? null : variants.find((v) => matches(v, colour, chosen(sizes)));

    const ready = !!(variant && variant.available);

    // The price changing is easy to miss when the eye is on the size that
    // was just pressed, so it says so.
    if (price) {
      const next = variant ? variant.price : basePrice;
      if (next !== price.textContent.trim()) {
        price.textContent = next;
        price.classList.remove('price-changed');
        void price.offsetWidth;
        price.classList.add('price-changed');
      }
    }
    if (idField) idField.value = ready ? variant.id : '';

    // aria-disabled rather than disabled, because the button has somewhere
    // to take you when it says Choose Options. The markup ships disabled so
    // that without this file the form still cannot be submitted half
    // answered; from here on the state is carried by the attribute.
    if (submit) {
      submit.disabled = false;
      submit.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }

    // The button says what is missing rather than only being unavailable:
    // a choice that has not been made, a variant that cannot be bought, or
    // the thing it is there to do.
    if (submitLabel) {
      if (!variant) submitLabel.textContent = needsColour || needsSize ? chooseLabel : soldLabel;
      else submitLabel.textContent = variant.available ? addLabel : soldLabel;
    }
  };

  /* The name fills with the colour that was asked for while its
   * photographs are on their way, and empties again when the first one is
   * there. Same idea as the cards, and the same band: straight from the
   * swatch the colour does not work at either end, so the hue is kept and
   * only the lightness is moved until it can be read.
   *
   * The hex is taken off the swatch itself. Liquid paints it there from the
   * colour's own name, so there is no second list of colours here to fall
   * out of step with that one.
   */
  const heading = document.querySelector('.heading-product');
  let waitTimer = null;

  const INK_FLOOR = 0.36;
  const INK_CEILING = 0.72;

  const inkFor = (input) => {
    const dot = input.closest('label') && input.closest('label').querySelector('[style*="background-color"]');
    if (!dot) return null;
    const found = getComputedStyle(dot).backgroundColor.match(/\d+/g);
    if (!found || found.length < 3) return null;

    let rgb = found.slice(0, 3).map(Number);
    const light = (c) => (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
    const mix = (c, towards, amount) => c.map((v) => Math.round(v + (towards - v) * amount));
    for (let i = 0; i < 9 && light(rgb) < INK_FLOOR; i += 1) rgb = mix(rgb, 255, 0.1);
    for (let i = 0; i < 9 && light(rgb) > INK_CEILING; i += 1) rgb = mix(rgb, 0, 0.1);
    return 'rgb(' + rgb.join(', ') + ')';
  };

  const settled = () => {
    clearTimeout(waitTimer);
    if (heading) heading.removeAttribute('data-loading');
  };

  const waitFor = (input) => {
    if (!heading) return;
    clearTimeout(waitTimer);
    const ink = inkFor(input);
    // Only after 120ms: a photograph already in the cache is there in a few
    // milliseconds, and a flash of colour on every swatch is noise.
    waitTimer = setTimeout(() => {
      const first = shown()[0];
      const img = first && first.querySelector('img');
      if (!img || (img.complete && img.naturalWidth > 0)) return;
      if (ink) heading.style.setProperty('--text-fill-loading', ink);
      else heading.style.removeProperty('--text-fill-loading');
      heading.dataset.loading = '';
      img.addEventListener('load', settled, { once: true });
      img.addEventListener('error', settled, { once: true });
    }, 120);
  };

  colours.forEach((input) => {
    input.addEventListener('change', () => {
      showGroup(input.value);
      waitFor(input);
      refresh();
    });
  });

  sizes.forEach((input) => input.addEventListener('change', refresh));

  // Pressing Choose Options takes the visitor to the choice that is
  // missing rather than doing nothing: the colour if there is no colour
  // yet, otherwise the size.
  const form = submit ? submit.closest('form') : null;
  if (form) {
    form.addEventListener('submit', (event) => {
      if (submit.getAttribute('aria-disabled') !== 'true') return;
      event.preventDefault();
      // The cart drawer listens for this on the document. Without stopping
      // it here, it would take a submission with no variant in it, fail,
      // and fall back to letting the browser post to /cart/add.
      event.stopPropagation();

      const needsColour = colourPos >= 0 && !chosen(colours);
      const wanted = needsColour ? colours : sizes;
      const target = wanted.find((input) => !input.disabled);
      if (!target) return;

      // Say which one, by the name the page gives it, so the sentence
      // matches the label the visitor is looking for.
      if (missing) {
        missing.textContent = needsColour ? colourMessage : sizeMessage;
        missing.classList.remove('hidden');
      }

      // Focus first, then scroll: moving focus cancels a smooth scroll
      // that is still running, so done the other way round the page never
      // went anywhere.
      target.focus({ preventScroll: true });

      const block = target.closest('[data-pdp-size-wrap]') || target.parentElement;

      // A blink rather than a ring that stays: beside chips that mark
      // being chosen with an outline, one that lingers says the wrong
      // thing.
      block.classList.remove('wants-a-choice');
      void block.offsetWidth;
      block.classList.add('wants-a-choice');
      const group = block.closest('div');
      // Instant, not smooth: a smooth scroll started from inside a submit
      // handler was being dropped, and a jump that always happens beats an
      // animation that sometimes does.
      (group || block).scrollIntoView({ block: 'center' });
    });
  }

  /* ---- quantity -------------------------------------------------- */

  /* ---- the lightbox ---------------------------------------------- */

  // The photograph on its own, at the size it was uploaded. It shows the
  // run the gallery is showing, which is the colour that was chosen, so
  // what goes in it is read at the moment it opens rather than written out
  // in advance.
  const box = document.querySelector('[data-lightbox]');
  if (box && typeof box.showModal === 'function') {
    const boxImage = box.querySelector('[data-lightbox-image]');
    const zoom = document.querySelector('[data-pdp-zoom]');
    let boxAt = 0;

    const paintBox = () => {
      const visible = shown();
      if (!visible.length || !boxImage) return;
      boxAt = ((boxAt % visible.length) + visible.length) % visible.length;
      const slide = visible[boxAt];
      boxImage.src = slide.dataset.full || slide.querySelector('img').src;
      boxImage.alt = slide.dataset.alt || '';

      const many = visible.length > 1;
      box.querySelectorAll('[data-lightbox-prev],[data-lightbox-next]').forEach((b) => {
        b.hidden = !many;
      });
    };

    if (zoom) {
      zoom.addEventListener('click', () => {
        boxAt = at;
        paintBox();
        box.showModal();
      });
    }

    box.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-close]')) {
        box.close();
        return;
      }
      if (event.target.closest('[data-lightbox-prev]')) {
        boxAt -= 1;
        paintBox();
        return;
      }
      if (event.target.closest('[data-lightbox-next]')) {
        boxAt += 1;
        paintBox();
        return;
      }
      // Anywhere else that is not the photograph closes it.
      if (!event.target.closest('img')) box.close();
    });

    // Leaving it, the gallery underneath goes to the photograph that was
    // being looked at, so closing does not lose your place.
    box.addEventListener('close', () => goTo(boxAt));
  }

  const qty = document.querySelector('[data-pdp-qty]');
  const down = document.querySelector('[data-qty-down]');
  const up = document.querySelector('[data-qty-up]');
  const stepQty = (by) => {
    if (!qty) return;
    const value = Math.max(1, (parseInt(qty.value, 10) || 1) + by);
    qty.value = value;
    if (down) down.disabled = value <= 1;
  };
  if (down) {
    down.disabled = true;
    down.addEventListener('click', () => stepQty(-1));
  }
  if (up) up.addEventListener('click', () => stepQty(1));
  if (qty) qty.addEventListener('change', () => stepQty(0));

  /* ---- the size guide -------------------------------------------- */

  // Both unit tables are already in the page; this only opens the panel and
  // says which of the two the stylesheet should show. Nothing is fetched and
  // nothing is rebuilt, so a reader with no JavaScript still has the
  // measurements, both sets of them, further down the document.
  const guide = document.querySelector('[data-size-guide]');
  if (guide && typeof guide.showModal === 'function') {
    const opener = document.querySelector('[data-size-guide-open]');

    // The chart is generic: it covers a garment, not this product. So every
    // row is measured against what is actually for sale before the table is
    // shown, and again whenever a colour changes underneath it.
    //
    // A row whose first cell names no size is the heading. A row naming a
    // size this product does not carry, or one the chosen colour has sold
    // out of, is struck through. What is left can be clicked, but only once
    // a colour is settled: which sizes exist depends on the colour, so
    // offering the choice before that would be offering a guess.
    const rowSize = (row) => {
      const first = row.cells && row.cells[0];
      return first ? first.textContent.trim() : '';
    };

    const gradeGuide = () => {
      const settled = colourPos < 0 || !!chosen(colours);
      guide.querySelectorAll('.size-guide-table tr').forEach((row) => {
        delete row.dataset.pick;
        delete row.dataset.muted;
        row.removeAttribute('tabindex');
        row.removeAttribute('role');
        row.removeAttribute('aria-label');

        const label = rowSize(row);
        if (!label) return;

        const input = sizes.find(
          (candidate) => candidate.value.trim().toLowerCase() === label.toLowerCase()
        );
        if (!input || input.disabled) {
          row.dataset.muted = '';
          return;
        }
        if (!settled) return;

        row.dataset.pick = input.value;
        row.tabIndex = 0;
        row.setAttribute('role', 'button');
        row.setAttribute('aria-label', `Choose size ${input.value}`);
      });
    };

    const pickRow = (row) => {
      const input = sizes.find((candidate) => candidate.value === row.dataset.pick);
      if (!input || input.disabled) return;
      input.checked = true;
      refresh();
      guide.close();
      // Straight to the thing they came here to be able to press.
      if (submit && !submit.disabled) submit.focus();
    };

    if (opener) {
      opener.addEventListener('click', () => {
        gradeGuide();
        guide.showModal();
      });
    }

    // A colour chosen while the chart is open changes which rows are real.
    [...colours, ...sizes].forEach((input) =>
      input.addEventListener('change', () => {
        if (guide.open) gradeGuide();
      })
    );

    guide.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const row = event.target.closest('tr[data-pick]');
      if (!row) return;
      event.preventDefault();
      pickRow(row);
    });

    guide.addEventListener('click', (event) => {
      if (event.target.closest('[data-size-guide-close]')) {
        guide.close();
        return;
      }

      const row = event.target.closest('tr[data-pick]');
      if (row) {
        pickRow(row);
        return;
      }

      const unit = event.target.closest('[data-size-guide-unit]');
      if (unit) {
        guide.dataset.unit = unit.dataset.sizeGuideUnit;
        guide.querySelectorAll('[data-size-guide-unit]').forEach((b) => {
          const on = b === unit;
          b.classList.toggle('is-chosen', on);
          b.setAttribute('aria-pressed', String(on));
        });
        return;
      }

      // The backdrop is the dialog itself: a click that landed on no child
      // of the panel is a click outside it.
      if (!event.target.closest('.size-guide-panel')) guide.close();
    });
  }

  // The gallery opens on a colour even though none is chosen yet: a track
  // holding every colour at once is what the visitor was complaining about,
  // and the first photograph belongs to the first colour anyway.
  const firstColour = slides.length ? slides[0].dataset.colour : null;
  if (firstColour && firstColour !== '-') showGroup(firstColour);
  else paint();

  refresh();
})();
